import React from 'react';
import { X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const Privacy = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isModal = location.state?.isModal;

  const handleClose = () => {
    if (isModal) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  React.useEffect(() => {
    if (isModal) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isModal]);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModal) {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModal]);

  const content = (
    <div className={`${isModal ? '' : 'min-h-screen'} bg-black`}>
      <div className={`${isModal ? 'max-h-[90vh] overflow-y-auto' : ''} relative`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="absolute inset-0 opacity-5 pointer-events-none">
            <div className="h-full w-full" style={{
              backgroundImage: `
                linear-gradient(rgba(0, 255, 255, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0, 255, 255, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '40px 40px'
            }}></div>
          </div>

          <div className="relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#00ffff] via-[#ff00ff] to-[#00ffff] rounded-xl blur opacity-20" />

            <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-2 border-[#00ffff] rounded-xl p-6 sm:p-8 lg:p-12">
              <div className="absolute top-3 left-3 w-4 h-4 border-l-2 border-t-2 border-[#00ffff]"></div>
              <div className="absolute top-3 right-3 w-4 h-4 border-r-2 border-t-2 border-[#00ffff]"></div>
              <div className="absolute bottom-3 left-3 w-4 h-4 border-l-2 border-b-2 border-[#00ffff]"></div>
              <div className="absolute bottom-3 right-3 w-4 h-4 border-r-2 border-b-2 border-[#00ffff]"></div>

              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-lg bg-[#af00ff] hover:bg-[#8f00df] text-white transition-all duration-300 hover:scale-110 z-10 border border-[#00ffff]"
                aria-label="Close privacy policy"
              >
                <X className="h-5 w-5" />
              </button>

              <div className="space-y-8">
                <div className="text-center border-b-2 border-[#af00ff]/30 pb-6">
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-retro text-[#00ffff] drop-shadow-[0_0_10px_#00ffff] mb-3 tracking-wider">
                    PRIVACY POLICY
                  </h1>
                  <p className="text-[#ff00ff] text-lg sm:text-xl font-mono-retro drop-shadow-[0_0_5px_#ff00ff]">
                    AI & Web Solutions IOM
                  </p>
                  <p className="text-[#00ff41] text-sm sm:text-base font-mono-retro drop-shadow-[0_0_3px_#00ff41] mt-2">
                    Last updated: 14/11/2025
                  </p>
                </div>

                <div className="space-y-6 text-gray-300">
                  <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed">
                    At AI & Web Solutions IOM, we are committed to protecting your privacy and handling your personal information with transparency and respect. This Privacy Policy explains the types of information we collect, how we use it, and the rights you have regarding your data.
                  </p>

                  <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed">
                    By using our website, submitting a form, or engaging with our services, you agree to the practices described in this policy.
                  </p>

                  <div className="space-y-4 pt-4">
                    <div className="border-l-4 border-[#af00ff] pl-4">
                      <h2 className="text-xl sm:text-2xl font-retro text-[#ff00ff] drop-shadow-[0_0_8px_#ff00ff] mb-3 tracking-wider">
                        1. INFORMATION WE COLLECT
                      </h2>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mb-3">
                        We may collect personal information when you:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-[#00ffff] font-mono-retro text-sm sm:text-base ml-4">
                        <li>Submit a form (including Meta Lead Forms)</li>
                        <li>Request a quote or strategy session</li>
                        <li>Contact us by phone, email, or social media</li>
                        <li>Use our website or interact with our content</li>
                      </ul>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mt-3 mb-2">
                        The information we collect may include:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-[#00ffff] font-mono-retro text-sm sm:text-base ml-4">
                        <li>Full name</li>
                        <li>Business name</li>
                        <li>Email address</li>
                        <li>Phone number</li>
                        <li>Website URL (if you have one)</li>
                        <li>Details about your project or enquiry</li>
                        <li>Any additional information you choose to provide</li>
                      </ul>
                      <p className="text-[#ff00ff] font-mono-retro text-sm sm:text-base leading-relaxed mt-3 font-semibold">
                        We do not collect unnecessary data.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#00ffff] pl-4">
                      <h2 className="text-xl sm:text-2xl font-retro text-[#00ffff] drop-shadow-[0_0_8px_#00ffff] mb-3 tracking-wider">
                        2. HOW WE USE YOUR INFORMATION
                      </h2>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mb-3">
                        We use your information to:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-[#00ffff] font-mono-retro text-sm sm:text-base ml-4">
                        <li>Respond to your enquiry</li>
                        <li>Deliver your free Website Strategy Plan</li>
                        <li>Provide quotes, proposals, or follow-ups</li>
                        <li>Improve our services and communication</li>
                        <li>Send service-related updates when necessary</li>
                      </ul>
                      <p className="text-[#ff00ff] font-mono-retro text-sm sm:text-base leading-relaxed mt-3 font-semibold">
                        We do not sell, rent, or share your personal information with third parties for marketing purposes.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#af00ff] pl-4">
                      <h2 className="text-xl sm:text-2xl font-retro text-[#ff00ff] drop-shadow-[0_0_8px_#ff00ff] mb-3 tracking-wider">
                        3. HOW WE STORE AND PROTECT YOUR DATA
                      </h2>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mb-3">
                        Your data is stored securely and accessed only by authorised personnel at AI & Web Solutions IOM.
                      </p>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mb-2">
                        We take appropriate technical and organisational measures to protect your information against:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-[#00ffff] font-mono-retro text-sm sm:text-base ml-4">
                        <li>Unauthorised access</li>
                        <li>Loss</li>
                        <li>Misuse</li>
                        <li>Alteration</li>
                      </ul>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mt-3">
                        All lead data submitted through Meta forms is stored in Meta's secure system and downloaded only when needed for your enquiry.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#00ffff] pl-4">
                      <h2 className="text-xl sm:text-2xl font-retro text-[#00ffff] drop-shadow-[0_0_8px_#00ffff] mb-3 tracking-wider">
                        4. HOW LONG WE KEEP YOUR INFORMATION
                      </h2>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mb-2">
                        We keep your information only for as long as it is necessary to:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-[#00ffff] font-mono-retro text-sm sm:text-base ml-4">
                        <li>Respond to your enquiry</li>
                        <li>Deliver requested services</li>
                        <li>Maintain accurate business records</li>
                      </ul>
                      <p className="text-[#ff00ff] font-mono-retro text-sm sm:text-base leading-relaxed mt-3 font-semibold">
                        You can request deletion of your data at any time.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#af00ff] pl-4">
                      <h2 className="text-xl sm:text-2xl font-retro text-[#ff00ff] drop-shadow-[0_0_8px_#ff00ff] mb-3 tracking-wider">
                        5. YOUR RIGHTS
                      </h2>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mb-2">
                        You have the right to:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-[#00ffff] font-mono-retro text-sm sm:text-base ml-4">
                        <li>Request a copy of the information we hold about you</li>
                        <li>Ask us to correct, update, or delete your information</li>
                        <li>Withdraw consent at any time</li>
                        <li>Request that we stop contacting you</li>
                      </ul>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mt-3">
                        To make a request, email us at:
                      </p>
                      <a
                        href="mailto:sean@aiwebsolutions.im"
                        className="text-[#ff00ff] font-mono-retro text-sm sm:text-base hover:text-[#00ffff] transition-colors duration-300 drop-shadow-[0_0_5px_currentColor] inline-block mt-1"
                      >
                        📧 sean@aiwebsolutions.im
                      </a>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mt-2">
                        We will respond promptly.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#00ffff] pl-4">
                      <h2 className="text-xl sm:text-2xl font-retro text-[#00ffff] drop-shadow-[0_0_8px_#00ffff] mb-3 tracking-wider">
                        6. COOKIES & WEBSITE TRACKING
                      </h2>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mb-3">
                        Our website may use cookies or analytics tools to improve user experience and performance. These tools may collect non-personal data such as:
                      </p>
                      <ul className="list-disc list-inside space-y-2 text-[#00ffff] font-mono-retro text-sm sm:text-base ml-4">
                        <li>Browser type</li>
                        <li>Pages visited</li>
                        <li>Time spent on site</li>
                      </ul>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mt-3">
                        This information is used only for improving website functionality and user experience.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#af00ff] pl-4">
                      <h2 className="text-xl sm:text-2xl font-retro text-[#ff00ff] drop-shadow-[0_0_8px_#ff00ff] mb-3 tracking-wider">
                        7. EXTERNAL LINKS
                      </h2>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed">
                        Our website may contain links to external sites. We are not responsible for the privacy practices of websites we do not control.
                      </p>
                    </div>

                    <div className="border-l-4 border-[#00ffff] pl-4">
                      <h2 className="text-xl sm:text-2xl font-retro text-[#00ffff] drop-shadow-[0_0_8px_#00ffff] mb-3 tracking-wider">
                        8. CONTACT US
                      </h2>
                      <p className="text-[#00ff41] font-mono-retro text-sm sm:text-base leading-relaxed mb-4">
                        If you have any questions about this Privacy Policy or how your data is handled, contact us at:
                      </p>
                      <div className="space-y-2 text-[#ff00ff] font-mono-retro text-sm sm:text-base">
                        <p className="drop-shadow-[0_0_3px_currentColor]">AI & Web Solutions IOM</p>
                        <p className="drop-shadow-[0_0_3px_currentColor]">Douglas, Isle of Man</p>
                        <a
                          href="mailto:sean@aiwebsolutions.im"
                          className="hover:text-[#00ffff] transition-colors duration-300 drop-shadow-[0_0_5px_currentColor] block"
                        >
                          📧 sean@aiwebsolutions.im
                        </a>
                        <a
                          href="tel:+4407624474095"
                          className="hover:text-[#00ffff] transition-colors duration-300 drop-shadow-[0_0_5px_currentColor] block"
                        >
                          📞 +44 7624 474095
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <>
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
          onClick={handleClose}
        />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl">
            {content}
          </div>
        </div>
      </>
    );
  }

  return content;
};

export default Privacy;
